import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  FlatList,
  BackHandler,
} from "react-native";
import { colors } from "@constants/theme";
import { useSelector, useDispatch } from "react-redux";
import { normalize } from "@constants/metrics";
import toast from "@utils/toast";
import Cross from "@assets/svg/Cross";
import { updateBasicInfo } from "@services/api";
import { actions } from "@reducers/authReducer";
import SuccessModal from "@screens/RejectedCl/Success";
import analytics from "@services/analytics";
import { PROFILE_EVENTS } from "@constants/analyticsEventsCT";

import t from "@constants/localization";
import ProgressBar from "./components/ProgressBar";
import DropdownInputView from "./dropdownInputView";
import TextInputView from "./textInputView";
import WheelPickerView from "./wheelPickerView";
import SelectInputView from "./selectInputView";

const PATH = "screens/CLProfile/index";

const CTEventMapping = {
  gender: PROFILE_EVENTS.PROFILE_GENDER_SAVE,
  age: PROFILE_EVENTS.PROFILE_AGE_SAVE,
  occupation: PROFILE_EVENTS.PROFILE_OCCUPATION_SAVE,
  availability_hours: PROFILE_EVENTS.PROFILE_AVAILABILITY_SAVE,
  income: PROFILE_EVENTS.PROFILE_INCOME_SAVE,
  source: PROFILE_EVENTS.PROFILE_SOURCE_SAVE,
  no_of_family_members: PROFILE_EVENTS.PROFILE_FAMILY_MEMBERS_SAVE,
  grocery_spending: PROFILE_EVENTS.PROFILE_GROCERY_SAVE,
  vehicle: PROFILE_EVENTS.PROFILE_VEHICLE_SAVE,
  alternate_phone: PROFILE_EVENTS.PROFILE_ALTERNATE_PHONE_SAVE,
};

export default function EditBasicInfo({ navigation, route }) {
  const { key: DEFAULT_KEY, source: CT_SOURCE = "" } = route.params || {};
  const flatlistRef = useRef();
  const dispatch = useDispatch();
  const { clDropDownOptions = [], ...userDetails } = useSelector(
    ({ auth: { user } }) => user,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [updatedPayload, setUpdatedPayload] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [alternatePhNoError, setAlternatePhNoError] = useState(false);
  const totalItemsForPercent = clDropDownOptions.filter((elem) => {
    return !elem.is_skipable;
  });

  const showToast = (errorMessage) => {
    toast.showLongToast(errorMessage);
  };

  const getInitialData = () => {
    let initialData = {};
    clDropDownOptions.forEach((el, i) => {
      initialData = Object.assign(initialData, {
        [el.name]: userDetails[el.name],
      });
    });
    return initialData;
  };

  const backhandler = useCallback(() => {
    dispatch(actions.setUpdatedBasicInfo(updatedPayload));
    return false;
  }, [updatedPayload, navigation]);

  useEffect(() => {
    const subscription = BackHandler.addEventListener("hardwareBackPress", backhandler);
    return () => {
      subscription.remove();
    };
  }, [backhandler]);

  const goBackHandler = () => {
    dispatch(actions.setUpdatedBasicInfo(updatedPayload));
    navigation.goBack();
  };

  const keyExtractor = (item, index) => {
    return `${index}`;
  };

  const handleProfileInfoSave = (payload = {}, isLast) => {
    const initialdata = getInitialData();
    const finalPayload = { ...initialdata, ...updatedPayload, ...payload };
    // in case api fails reset to previous state
    const payloadCopy = updatedPayload;
    setUpdatedPayload({ ...updatedPayload, ...finalPayload });
    if (
      finalPayload.alternate_phone &&
      finalPayload.alternate_phone === userDetails.phone_number
    ) {
      setUpdatedPayload(payloadCopy);
      setAlternatePhNoError(true);
      return;
    }
    setAlternatePhNoError(false);
    updateBasicInfo(finalPayload)
      .then((res) => {
        if (isLast) {
          setIsSuccess(true);
          const payloadKeys = Object.keys(payload);
          payloadKeys.map((key) => {
            analytics.trackEvent(`Profile ${key} saved`, {
              source: CT_SOURCE,
            });
          });
        }
      })
      .catch((err) => {
        console.warn("some error");
        setUpdatedPayload(payloadCopy);
        showToast(err.message);
      });
  };

  const handleOnSkip = (length, extradata, name) => {
    const items = getItems();
    if (items.length - 1 === length) {
      handleProfileInfoSave(extradata);
      goBackHandler();
    } else {
      if (currentIndex < items.length - 1) {
        flatlistRef.current.scrollToIndex({
          animated: true,
          index: currentIndex + 1,
        });
      }
      setCurrentIndex(currentIndex + 1);
      handleProfileInfoSave(extradata);
    }
  };

  const handleOnSubmit = (length, extradata, name) => {
    const items = getItems();
    if (CTEventMapping[name]) {
      analytics.trackEvent(CTEventMapping[name], {
        screen_name: route.name,
        ...extradata,
      });
    }
    if (items.length - 1 === length) {
      handleProfileInfoSave(extradata, true);
    } else {
      if (currentIndex < items.length - 1) {
        flatlistRef.current.scrollToIndex({
          animated: true,
          index: currentIndex + 1,
        });
      }
      setCurrentIndex(currentIndex + 1);
      handleProfileInfoSave(extradata);
    }
  };

  const getItems = () => {
    const items = [];
    const initialdata = getInitialData();

    clDropDownOptions.map((option) => {
      const value = initialdata[option.name];
      if (initialdata[option.name] !== null && DEFAULT_KEY !== option.name) {
        return;
      }
      const method = DEFAULT_KEY === option.name ? "unshift" : "push";
      if (option.show_profile) {
        switch (option.component) {
          case "CustomTextInput":
            items[method](
              <TextInputView
                data={option}
                value={value}
                multiline={true}
                // numberOfLines={5}
                index={items.length}
                alternatePhNoError={alternatePhNoError}
                onSubmit={handleOnSubmit}
                onSkip={handleOnSkip}
              />,
            );
            break;
          case "CustomDropDownInput":
            items[method](
              <DropdownInputView
                data={option}
                value={value === -1 ? null : value}
                select={!!option.oneClick}
                index={items.length}
                onSubmit={handleOnSubmit}
                onSkip={handleOnSkip}
              />,
            );
            break;
          case "CustomWheelPicker":
            items[method](
              <WheelPickerView
                data={option}
                value={value}
                index={items.length}
                onSubmit={handleOnSubmit}
              />,
            );
            break;
          // case "CustomTextInput":
          //   items.push(<TextInputView index={items.length} onSubmit={handleOnSubmit} />)
        }
      }
    });

    return items;
  };

  const _renderItem = ({ item, index }) => {
    const { width } = Dimensions.get("window");
    return (
      <View
        style={{
          width,
        }}
      >
        {item}
      </View>
    );
  };

  const totalItems = getItems();
  const allFieldsFilled = totalItems.length === 0;
  const showEditView = !!DEFAULT_KEY;

  return (
    <View style={{ flex: 1, backgroundColor: "white" }}>
      <SuccessModal
        message={t(`${PATH}/profileUpdateSuccessfully`)}
        onPress={goBackHandler}
        visible={isSuccess || allFieldsFilled}
      />
      <View style={styles.topNavContainer}>
        {!showEditView && (
          <View style={{ flex: 1, marginRight: 40 }}>
            <ProgressBar
              totalItems={totalItemsForPercent.length}
              completed={
                totalItemsForPercent.length - totalItems.length + currentIndex
              }
            />
          </View>
        )}
        <TouchableOpacity onPress={goBackHandler}>
          <Cross width={18} height={18} stroke="0.1" />
        </TouchableOpacity>
      </View>
      <View
        style={{
          flex: 1,
        }}
      >
        <FlatList
          ref={flatlistRef}
          data={totalItems}
          keyExtractor={keyExtractor}
          keyboardShouldPersistTaps="always"
          pagingEnabled={true}
          horizontal
          scrollEnabled={false}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          directionalLockEnabled
          renderItem={_renderItem}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  topNavContainer: {
    flexDirection: "row",
    paddingVertical: normalize(18),
    paddingHorizontal: normalize(11),
    backgroundColor: colors.white,
    marginHorizontal: 6,
  },
  arrowCont: {
    marginLeft: 40,
  },
});
